import { Download } from "lucide-react";
import { useMemo, useState } from "react";

import { API_URL } from "@/api/application-api";
import CodeEditor from "@/components/CodeEditor";
import { languages } from "@/components/CodeEditor/constants";
import { LanguageType } from "@/components/CodeEditor/types";
import {
  getProviderSelectionFromProvider,
  SkillCollectionPresetValue,
  VENDOR_AGNOSTIC_PROVIDER,
} from "@/components/SkillCollection/skill-presets";
import { Button } from "@/components/ui/button";
import Checkbox from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

interface PullSkillsPopoverProps {
  collectionName: string;
  versionNumber: number | null;
  provider: string | null;
}

const DEFAULT_API_URL = "https://api.promptlayer.com";

const getProviderOutputDirectory = (provider: string | null): string => {
  switch (getProviderSelectionFromProvider(provider)) {
    case SkillCollectionPresetValue.ClaudeCode:
      return ".claude";
    case SkillCollectionPresetValue.OpenAI:
      return ".agents";
    case SkillCollectionPresetValue.OpenClaw:
      return ".openclaw";
    case VENDOR_AGNOSTIC_PROVIDER:
    default:
      return "skills";
  }
};

const getProviderRootPrefix = (provider: string | null): string | null => {
  switch (getProviderSelectionFromProvider(provider)) {
    case SkillCollectionPresetValue.ClaudeCode:
      return ".claude/";
    case SkillCollectionPresetValue.OpenAI:
      return ".agents/";
    case SkillCollectionPresetValue.OpenClaw:
      return ".openclaw/";
    case VENDOR_AGNOSTIC_PROVIDER:
    default:
      return null;
  }
};

const buildJavaScriptPullCall = (
  collectionName: string,
  versionNumber: number | null,
  asZip: boolean,
) => {
  const options: string[] = [];

  if (versionNumber !== null) {
    options.push(`version: ${versionNumber}`);
  }

  if (asZip) {
    options.push(`format: "zip"`);
  }

  const quotedCollectionName = JSON.stringify(collectionName);

  if (options.length === 0) {
    return `await client.skills.pull(${quotedCollectionName})`;
  }

  return `await client.skills.pull(${quotedCollectionName}, { ${options.join(", ")} })`;
};

const buildPythonPullCall = (
  collectionName: string,
  versionNumber: number | null,
  asZip: boolean,
) => {
  const args = [JSON.stringify(collectionName)];

  if (versionNumber !== null) {
    args.push(`version=${versionNumber}`);
  }

  if (asZip) {
    args.push(`format="zip"`);
  }

  return `client.skills.pull(${args.join(", ")})`;
};

interface GenerateSnippetOptions {
  collectionName: string;
  versionNumber: number | null;
  provider: string | null;
  lang: LanguageType;
  asZip: boolean;
}

const generateSnippet = ({
  collectionName,
  versionNumber,
  provider,
  lang,
  asZip,
}: GenerateSnippetOptions): string => {
  const pythonBaseUrlLines =
    API_URL === DEFAULT_API_URL ? [] : [`    base_url=${JSON.stringify(API_URL)},`];
  const javaScriptBaseUrlLines =
    API_URL === DEFAULT_API_URL ? [] : [`  baseURL: ${JSON.stringify(API_URL)},`];
  const outputDirectory = getProviderOutputDirectory(provider);
  const providerRootPrefix = getProviderRootPrefix(provider);
  const pythonPrefixLine = providerRootPrefix
    ? [`prefix = ${JSON.stringify(providerRootPrefix)}`]
    : [`prefix = None`];
  const javaScriptPrefixLine = providerRootPrefix
    ? [`const prefix = ${JSON.stringify(providerRootPrefix)};`]
    : [`const prefix = null;`];

  switch (lang) {
    case "PYTHON":
      return asZip
        ? [
            `# pip install -U promptlayer`,
            `import os`,
            `from pathlib import Path`,
            ``,
            `from promptlayer import PromptLayer`,
            ``,
            `client = PromptLayer(`,
            `    api_key=os.environ["PROMPTLAYER_API_KEY"],`,
            ...pythonBaseUrlLines,
            `)`,
            ``,
            `zip_archive = ${buildPythonPullCall(collectionName, versionNumber, asZip)}`,
            `if not isinstance(zip_archive, bytes):`,
            `    raise ValueError("Expected a zip archive.")`,
            ``,
            `output_dir = Path(${JSON.stringify(outputDirectory)})`,
            `output_dir.mkdir(parents=True, exist_ok=True)`,
            `(output_dir / "skills.zip").write_bytes(zip_archive)`,
          ].join("\n")
        : [
            `# pip install -U promptlayer`,
            `import os`,
            `from pathlib import Path`,
            ``,
            `from promptlayer import PromptLayer`,
            ``,
            `client = PromptLayer(`,
            `    api_key=os.environ["PROMPTLAYER_API_KEY"],`,
            ...pythonBaseUrlLines,
            `)`,
            ``,
            `result = ${buildPythonPullCall(collectionName, versionNumber, asZip)}`,
            `if result is None:`,
            `    raise ValueError("No skill collection returned.")`,
            ``,
            `output_dir = Path(${JSON.stringify(outputDirectory)})`,
            `output_dir.mkdir(parents=True, exist_ok=True)`,
            ...pythonPrefixLine,
            ``,
            `for relative_path, content in result["files"].items():`,
            `    target_relative_path = (`,
            `        relative_path[len(prefix) :] if prefix and relative_path.startswith(prefix) else relative_path`,
            `    )`,
            `    output_path = output_dir / target_relative_path`,
            `    output_path.parent.mkdir(parents=True, exist_ok=True)`,
            `    output_path.write_text(content, encoding="utf-8")`,
          ].join("\n");
    case "JAVASCRIPT":
      return asZip
        ? [
            `// npm install promptlayer`,
            `import { PromptLayer } from "promptlayer";`,
            `import { mkdir, writeFile } from "node:fs/promises";`,
            `import path from "node:path";`,
            ``,
            `const client = new PromptLayer({`,
            `  apiKey: process.env.PROMPTLAYER_API_KEY,`,
            ...javaScriptBaseUrlLines,
            `});`,
            ``,
            `const zipArchive = ${buildJavaScriptPullCall(collectionName, versionNumber, asZip)};`,
            `if (!(zipArchive instanceof ArrayBuffer)) {`,
            `  throw new Error("Expected a zip archive.");`,
            `}`,
            ``,
            `const outputDir = ${JSON.stringify(outputDirectory)};`,
            `await mkdir(outputDir, { recursive: true });`,
            `await writeFile(path.join(outputDir, "skills.zip"), Buffer.from(zipArchive));`,
          ].join("\n")
        : [
            `// npm install promptlayer`,
            `import { PromptLayer } from "promptlayer";`,
            `import { mkdir, writeFile } from "node:fs/promises";`,
            `import path from "node:path";`,
            ``,
            `const client = new PromptLayer({`,
            `  apiKey: process.env.PROMPTLAYER_API_KEY,`,
            ...javaScriptBaseUrlLines,
            `});`,
            ``,
            `const result = ${buildJavaScriptPullCall(collectionName, versionNumber, asZip)};`,
            `if (!result || result instanceof ArrayBuffer) {`,
            `  throw new Error("Expected a JSON skill collection response.");`,
            `}`,
            ``,
            `const outputDir = ${JSON.stringify(outputDirectory)};`,
            `await mkdir(outputDir, { recursive: true });`,
            ...javaScriptPrefixLine,
            ``,
            `for (const [relativePath, content] of Object.entries(result.files)) {`,
            `  const targetRelativePath = prefix && relativePath.startsWith(prefix)`,
            `    ? relativePath.slice(prefix.length)`,
            `    : relativePath;`,
            `  const outputPath = path.join(outputDir, targetRelativePath);`,
            `  await mkdir(path.dirname(outputPath), { recursive: true });`,
            `  await writeFile(outputPath, content, "utf8");`,
            `}`,
          ].join("\n");
    default:
      return "";
  }
};

export const PullSkillsPopover = ({
  collectionName,
  versionNumber,
  provider,
}: PullSkillsPopoverProps) => {
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageType>(languages[0]);
  const [asZip, setAsZip] = useState(false);

  const code = useMemo(
    () =>
      generateSnippet({
        collectionName,
        versionNumber,
        provider,
        lang: selectedLanguage,
        asZip,
      }),
    [collectionName, versionNumber, provider, selectedLanguage, asZip],
  );
  const versionLabel = versionNumber ? `Version ${versionNumber}` : "Latest version";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="ghost" size="xs" className="shrink-0">
          <Download className="mr-1 h-3.5 w-3.5" />
          Pull via SDK
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[560px] p-0" side="bottom" align="end">
        <div className="flex flex-col gap-4 p-4">
          <div>
            <div>
              <p className="text-sm font-semibold text-gray-900">Pull skills with the SDK</p>
              <p className="mt-1 text-xs leading-5 text-gray-500">
                Use the JavaScript or Python PromptLayer SDK to pull {versionLabel.toLowerCase()}{" "}
                for <span className="font-medium text-gray-700">{collectionName}</span> as a local
                folder of files or a `.zip` archive.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3">
            <p className="max-w-[386px] text-xs text-gray-500">
              These SDK snippets update automatically based on the selected format.
            </p>
            <Checkbox
              id="pull-skills-zip"
              label="Download as .zip"
              checked={asZip}
              onChange={e => setAsZip(e.target.checked)}
            />
          </div>

          <CodeEditor
            languages={languages}
            selectedLanguage={selectedLanguage}
            handleLanguageChange={setSelectedLanguage}
            controlsType="tabs"
            code={code}
            showRuntimeDetails={false}
            showFullScreenButton={false}
            height="260px"
            editorOptions={{
              fontSize: 12,
              lineHeight: 18,
              padding: { top: 12, bottom: 12 },
              renderLineHighlight: "none",
              readOnly: true,
              wordWrap: "off",
              scrollbar: { horizontal: "auto", vertical: "hidden" },
            }}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
};
